import type { CSSProperties } from "react";

export type FileKind = {
  icon: string;
  color: "success" | "danger" | "primary" | "warning" | "info" | "secondary";
  kindLabel: string;
  isImage: boolean;
};

export function getFileExtension(filenameOrUrl: string): string {
  try {
    if (/^https?:\/\//i.test(filenameOrUrl)) {
      const pathname = new URL(filenameOrUrl).pathname;
      const base = pathname.split("?")[0];
      const i = base.lastIndexOf(".");
      return i >= 0 ? base.slice(i + 1).toLowerCase() : "";
    }
  } catch {
    /* fall through */
  }
  const base = filenameOrUrl.split("?")[0];
  const i = base.lastIndexOf(".");
  return i >= 0 ? base.slice(i + 1).toLowerCase() : "";
}

export function getFileKind(filenameOrUrl: string): FileKind {
  const ext = getFileExtension(filenameOrUrl);

  if (["jpg", "jpeg", "png", "gif", "webp", "heif", "bmp", "svg"].includes(ext)) {
    return { icon: "ri-image-2-line", color: "success", kindLabel: "Image", isImage: true };
  }
  if (ext === "pdf") {
    return { icon: "ri-file-pdf-line", color: "danger", kindLabel: "PDF", isImage: false };
  }
  if (["doc", "docx", "txt", "rtf"].includes(ext)) {
    return { icon: "ri-file-word-line", color: "primary", kindLabel: "Document", isImage: false };
  }
  if (["xls", "xlsx", "csv"].includes(ext)) {
    return { icon: "ri-file-excel-line", color: "success", kindLabel: "Spreadsheet", isImage: false };
  }
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) {
    return { icon: "ri-file-zip-line", color: "warning", kindLabel: "Archive", isImage: false };
  }
  if (["mp4", "webm", "mov", "avi"].includes(ext)) {
    return { icon: "ri-movie-2-line", color: "info", kindLabel: "Video", isImage: false };
  }
  return {
    icon: "ri-attachment-2",
    color: "secondary",
    kindLabel: ext ? ext.toUpperCase() : "File",
    isImage: false,
  };
}

interface FileTypeIconProps {
  /** Filename or full URL — used to derive the extension. */
  filename: string;
  /** When provided and the file is an image, renders a preview <img> instead of the icon. */
  imageSrc?: string;
  className?: string;
  style?: CSSProperties;
}

export function FileTypeIcon({ filename, imageSrc, className = "", style }: FileTypeIconProps) {
  const { icon, color, isImage } = getFileKind(filename);
  if (isImage && imageSrc) {
    return (
      <img
        src={imageSrc}
        alt=""
        className={`object-fit-cover ${className}`.trim()}
        style={style}
      />
    );
  }
  return <i className={`${icon} text-${color} ${className}`.trim()} style={style} />;
}
